import {
  type AgentContext,
  type ClientErrorEvent,
  FEEDBACK_CONTEXT_GROUPS,
  FEEDBACK_DESCRIPTION_MAX,
  type FeedbackContext,
  type FeedbackContextGroup,
  type FeedbackDraftView,
  type FeedbackType,
  type SendFeedbackDraftRequest,
  type SendFeedbackRequest,
} from "@esi/shared";
import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiError } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { t as translate } from "../i18n/i18n.ts";
import { MY_FEEDBACK_PATH, navigate } from "../router.tsx";
import {
  agentContextParts,
  currentFeedbackContext,
  draftRequest,
  feedbackRequest,
  pickContext,
} from "./context.ts";
import { useSendDraft } from "./drafts.ts";
import { FeedbackThrottled, useSendFeedback } from "./queries.ts";
import {
  type AttachedScreenshot,
  ScreenshotField,
  type ScreenshotSource,
} from "./ScreenshotField.tsx";
import { captureScreenshot, reachableRegions, regionsIn } from "./screenshot.ts";

/**
 * "Send feedback" (openspec change `in-app-feedback`, design D7): a bug or an idea in the user's
 * own words, and a "What will be sent" panel listing every context field as it will be stored,
 * each group with its own checkbox. The request is built from the object the panel shows.
 */
export function FeedbackDialog(props: {
  open: boolean;
  onClose: () => void;
  returnFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("feedback.title")}
      description={t("feedback.lead")}
      {...(props.returnFocus && { returnFocus: props.returnFocus })}
    >
      <SendFeedback onClose={props.onClose} />
    </AppDialog>
  );
}

/**
 * A feedback draft from a conversation, prefilled (feedback-from-conversation D3): the same form,
 * its type and description editable, and the server-built Assistant group in "What will be
 * sent" with its own checkbox. Sends through the draft, never `POST /api/feedback`.
 */
export function FeedbackDraftDialog(props: {
  draft: FeedbackDraftView;
  open: boolean;
  onClose: () => void;
  /** After the send: the card settles. */
  onSent?: () => void;
  returnFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("feedback.title")}
      description={
        props.draft.trigger === "answer_action" ? t("feedback.lead") : t("feedbackDraft.dialogLead")
      }
      {...(props.returnFocus && { returnFocus: props.returnFocus })}
    >
      <SendDraft draft={props.draft} onClose={props.onClose} onSent={props.onSent} />
    </AppDialog>
  );
}

function SendDraft(props: {
  draft: FeedbackDraftView;
  onClose: () => void;
  onSent?: (() => void) | undefined;
}) {
  const send = useSendDraft(props.draft);
  const context = useMemo(() => currentFeedbackContext(), []);
  return (
    <FeedbackForm
      context={context}
      draft={props.draft}
      pending={send.isPending}
      error={send.error}
      sent={send.isSuccess}
      onSend={() => {}}
      onSendDraft={(body) => send.mutate(body, { onSuccess: () => props.onSent?.() })}
      onClose={props.onClose}
    />
  );
}

/**
 * The page under the dialog: the app's root. Dialogs, menus and toasts render in portals
 * outside it, so the feedback dialog is never in its own screenshot.
 */
const appRoot = () => document.getElementById("root");

const pageScreenshots: ScreenshotSource = {
  regions: () => {
    const root = appRoot();
    return root ? regionsIn(root) : [];
  },
  reachable: (unmasked) => {
    const root = appRoot();
    return root ? reachableRegions(root, unmasked) : [];
  },
  capture: (unmasked) => {
    const root = appRoot();
    if (!root) {
      return Promise.reject(new Error("no page to capture"));
    }
    return captureScreenshot(root, { unmasked });
  },
};

/** Mounted while the dialog is open: the context is read once, when it opens. */
function SendFeedback({ onClose }: { onClose: () => void }) {
  const send = useSendFeedback();
  const context = useMemo(() => currentFeedbackContext(), []);
  return (
    <FeedbackForm
      context={context}
      pending={send.isPending}
      error={send.error}
      sent={send.isSuccess}
      screenshotFailed={send.data?.screenshot === "failed"}
      screenshots={pageScreenshots}
      onSend={(body, screenshot) => send.mutate({ body, screenshot })}
      onClose={onClose}
    />
  );
}

/** The form itself, without the request: what it would send goes to `onSend`. */
export function FeedbackForm(props: {
  context: FeedbackContext;
  pending: boolean;
  error: unknown;
  sent: boolean;
  /** The report was sent but its screenshot could not be attached. */
  screenshotFailed?: boolean;
  /** Where screenshots come from; without one, the dialog offers none. */
  screenshots?: ScreenshotSource;
  onSend: (body: SendFeedbackRequest, screenshot: AttachedScreenshot | null) => void;
  /** A draft from a conversation: prefilled, with the Assistant group (feedback-from-conversation). */
  draft?: FeedbackDraftView;
  onSendDraft?: (body: SendFeedbackDraftRequest) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [type, setType] = useState<FeedbackType>(props.draft?.type ?? "bug");
  const [description, setDescription] = useState(props.draft?.description ?? "");
  const [assistant, setAssistant] = useState(true);
  const [empty, setEmpty] = useState(false);
  const available = FEEDBACK_CONTEXT_GROUPS.filter((g) => props.context[g] !== undefined);
  const [ticked, setTicked] = useState<ReadonlySet<FeedbackContextGroup>>(() => new Set(available));
  // What the panel shows is exactly what is sent: one object for both.
  const preview = pickContext(props.context, ticked);
  // The previewed PNG itself is what is uploaded.
  const [screenshot, setScreenshot] = useState<AttachedScreenshot | null>(null);
  const [capturing, setCapturing] = useState(false);

  if (props.sent) {
    return (
      <div className="flex flex-col gap-5">
        <Notice tone="success" live>
          {t("feedback.sent")}
        </Notice>
        {props.screenshotFailed ? (
          <Notice tone="warning">{t("feedbackScreenshot.notAttached")}</Notice>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              props.onClose();
              navigate(MY_FEEDBACK_PATH);
            }}
          >
            {t("feedback.seeMine")}
          </Button>
          <Button type="button" onClick={props.onClose}>
            {t("app.close")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="flex min-w-0 flex-col gap-5"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (description.trim().length === 0) {
          setEmpty(true);
          return;
        }
        if (props.pending || capturing) {
          return;
        }
        if (props.draft && props.onSendDraft) {
          props.onSendDraft(draftRequest(type, description, preview, assistant));
        } else {
          props.onSend(feedbackRequest(type, description, preview), screenshot);
        }
      }}
    >
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-sm font-medium text-fg">{t("feedback.type")}</legend>
        <RadioGroup
          value={type}
          onValueChange={(value) => setType(value as FeedbackType)}
          className="flex flex-wrap gap-x-6 gap-y-2"
        >
          {(["bug", "idea"] as const).map((value) => (
            <div key={value} className="flex items-center gap-2">
              <RadioGroupItem id={`${id}-${value}`} value={value} />
              <Label htmlFor={`${id}-${value}`} className="font-normal">
                {t(`feedback.kinds.${value}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </fieldset>

      <div className="flex flex-col gap-2">
        <Label htmlFor={`${id}-description`}>
          {type === "bug" ? t("feedback.describeBug") : t("feedback.describeIdea")}
        </Label>
        <Textarea
          id={`${id}-description`}
          value={description}
          maxLength={FEEDBACK_DESCRIPTION_MAX}
          rows={5}
          aria-invalid={empty || undefined}
          aria-describedby={`${id}-hint${empty ? ` ${id}-empty` : ""}`}
          onChange={(e) => {
            setDescription(e.target.value);
            setEmpty(false);
          }}
        />
        <p id={`${id}-hint`} className="text-xs text-fg-muted">
          {t("feedback.hint")}
          {description.length > FEEDBACK_DESCRIPTION_MAX - 400
            ? ` ${t("feedback.remaining", { count: FEEDBACK_DESCRIPTION_MAX - description.length })}`
            : null}
        </p>
        {empty ? (
          <p id={`${id}-empty`} className="text-xs text-danger-fg">
            {t("feedback.empty")}
          </p>
        ) : null}
      </div>

      <fieldset className="flex min-w-0 flex-col gap-3 rounded-lg border border-line bg-surface-raised p-3">
        <legend className="px-1 text-sm font-medium text-fg">{t("feedback.whatIsSent")}</legend>
        <p className="text-xs text-fg-secondary">{t("feedback.whatIsSentLead")}</p>
        <ul className="flex min-w-0 flex-col gap-3">
          {FEEDBACK_CONTEXT_GROUPS.map((group) => (
            <ContextRow
              key={group}
              id={`${id}-${group}`}
              group={group}
              context={props.context}
              checked={ticked.has(group)}
              onChange={(checked) => {
                const next = new Set(ticked);
                if (checked) {
                  next.add(group);
                } else {
                  next.delete(group);
                }
                setTicked(next);
              }}
            />
          ))}
          {props.draft ? (
            <AssistantRow
              id={`${id}-assistant`}
              context={props.draft.agentContext}
              checked={assistant}
              onChange={setAssistant}
            />
          ) : null}
          {props.screenshots ? (
            <ScreenshotField
              source={props.screenshots}
              disabled={props.pending}
              onChange={setScreenshot}
              onBusy={setCapturing}
            />
          ) : null}
        </ul>
        {props.draft ? (
          <p className="text-xs text-fg-muted">{t("feedbackDraft.nothingAttached")}</p>
        ) : null}
      </fieldset>

      {props.error ? <Notice tone="danger">{failure(props.error)}</Notice> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={props.onClose}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={props.pending || capturing}>
          {props.pending ? t("feedback.sending") : t("feedback.send")}
        </Button>
      </div>
    </form>
  );
}

export function failure(error: unknown): string {
  if (error instanceof FeedbackThrottled) {
    return translate("feedback.throttled", { time: formatDateTime(error.retryAt.toISOString()) });
  }
  // An API without drafts (N-1, during a canary): the draft is kept.
  if (error instanceof ApiError && error.status === 404) {
    return translate("feedbackDraft.unavailable");
  }
  return error instanceof ApiError ? error.message : translate("errors.network");
}

/** One context group: its checkbox, and its values as they will be stored. */
function ContextRow(props: {
  id: string;
  group: FeedbackContextGroup;
  context: FeedbackContext;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const { context, group } = props;
  const present = context[group] !== undefined;
  return (
    <li className="flex min-w-0 items-start gap-2">
      <Checkbox
        id={props.id}
        className="mt-0.5"
        checked={present && props.checked}
        disabled={!present}
        onCheckedChange={(checked) => props.onChange(checked === true)}
        aria-describedby={`${props.id}-values`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Label htmlFor={props.id} className="font-normal">
          {t(`feedback.groups.${group}`)}
        </Label>
        <div
          id={`${props.id}-values`}
          className={props.checked && present ? "text-fg-secondary" : "text-fg-muted line-through"}
        >
          <GroupValues group={group} context={context} />
        </div>
      </div>
    </li>
  );
}

/**
 * The Assistant group of a draft: what the server built from the turn (role, tools, failures,
 * outcome), shown as stored, sent unless unticked.
 */
function AssistantRow(props: {
  id: string;
  context: AgentContext;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex min-w-0 items-start gap-2">
      <Checkbox
        id={props.id}
        className="mt-0.5"
        checked={props.checked}
        onCheckedChange={(checked) => props.onChange(checked === true)}
        aria-describedby={`${props.id}-values`}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Label htmlFor={props.id} className="font-normal">
          {t("feedback.groups.assistant")}
        </Label>
        <p
          id={`${props.id}-values`}
          className={cn(value, props.checked ? "text-fg-secondary" : "text-fg-muted line-through")}
        >
          {agentContextParts(props.context).join(" · ")}
        </p>
      </div>
    </li>
  );
}

const value = "break-words font-mono text-xs";

export function GroupValues({
  group,
  context,
}: {
  group: FeedbackContextGroup;
  context: FeedbackContext;
}) {
  const { t } = useTranslation();
  switch (group) {
    case "app": {
      const app = context.app;
      return (
        <p className={value}>
          {[app?.version, app?.build, app?.time ? formatDateTime(app.time) : undefined]
            .filter(Boolean)
            .join(" · ")}
        </p>
      );
    }
    case "route":
      return <p className={value}>{context.route}</p>;
    case "device": {
      const d = context.device;
      return d ? (
        <p className={value}>
          {[
            d.browserMajor !== undefined ? `${d.browser} ${d.browserMajor}` : d.browser,
            d.os,
            d.locale,
            t(`feedback.viewport.${d.viewport}`),
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null;
    }
    case "recentErrors":
      return context.recentErrors ? (
        <ErrorList errors={context.recentErrors} />
      ) : (
        <p className="text-xs">{t("feedback.noErrors")}</p>
      );
  }
}

/** Each error as stored: name, reduced message, route and frames (no stack text, no URL). */
export function ErrorList({ errors }: { errors: readonly ClientErrorEvent[] }) {
  return (
    <ol className="flex max-h-40 min-w-0 flex-col gap-1.5 overflow-y-auto">
      {errors.map((e, i) => (
        // Position keys: the list is a snapshot taken when the dialog opened.
        // biome-ignore lint/suspicious/noArrayIndexKey: a static snapshot, never reordered
        <li key={i} className={value}>
          {e.name}
          {e.message ? `: ${e.message}` : ""} · {e.route}
          {e.count > 1 ? ` ×${e.count}` : ""}
          {e.frames.length > 0 ? (
            <span className="block text-fg-muted">
              {e.frames.map((f) => `${f.file}:${f.line}:${f.col}`).join(" ")}
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
